import React, { useState, forwardRef, useImperativeHandle, useRef } from "react";
import { Modal, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { Gender } from "./orders";
import type { BeneficiaryVO, PolicyholderVO } from "./orders";
import CustomButton from "../../components/CustomButton";
import { BeneficiaryEditForm, BeneficiaryEditFormRef } from "./BeneficiaryEditForm";
import { INITIAL_BENEFICIARY } from "./constants";
import { updateBeneficiary, deleteBeneficiary, getDictLabel, COMMON_DICTS, getMetadataLabel } from "@ifa/utils";
import { useIOSConfirm } from "../../hooks";

interface BeneficiaryFormProps {
  data: BeneficiaryVO[];
  onChange: (data: BeneficiaryVO[]) => void;
  existingBeneficiaries?: BeneficiaryVO[];
  orderId?: number;
  onRefresh?: () => void;
  readOnly?: boolean;
  policyholder?: PolicyholderVO;
}

export interface BeneficiaryFormRef {
  validate: () => Promise<any>;
}

export const BeneficiaryForm = forwardRef<BeneficiaryFormRef, BeneficiaryFormProps>(({ data = [], onChange, existingBeneficiaries = [], orderId, onRefresh, readOnly = false, policyholder }, ref) => {
  const [view, setView] = useState<"list" | "edit">("list");
  const editFormRef = useRef<BeneficiaryEditFormRef>(null);
  const { confirm } = useIOSConfirm();

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingData, setEditingData] = useState<Partial<BeneficiaryVO>>({});
  const [selectModalVisible, setSelectModalVisible] = useState(false);

  const handleAdd = () => {
    setEditingIndex(null);
    setEditingData({ ...INITIAL_BENEFICIARY });
    setView("edit");
  };

  const handleSelectExisting = async (beneficiary: BeneficiaryVO) => {
    const newBeneficiary = {
      ...INITIAL_BENEFICIARY,
      basic: { ...beneficiary.basic },
      relationship: beneficiary.relationship,
      // Don't copy ID to avoid conflict with order-specific IDs
    };

    if (orderId) {
      try {
        await updateBeneficiary(orderId, { ...newBeneficiary, orderId });
        if (onRefresh) {
          onRefresh();
          setSelectModalVisible(false);
          return;
        }
      } catch (e) {
        console.error(e);
        // message.error("添加失敗");
        return;
      }
    }

    const newData = [...data, newBeneficiary];
    onChange(newData);
    setSelectModalVisible(false);
  };

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    setEditingData(data[index]);
    console.log("data[index]", data[index]);
    setView("edit");
  };

  const handleDelete = (index: number) => {
    confirm({
      title: "確認刪除",
      content: "確定要刪除這位受益人嗎？",
      okText: "確認",
      cancelText: "取消",
      onOk: async () => {
        const itemToDelete = data[index];
        if (orderId && itemToDelete.beneficiaryId) {
          try {
            await deleteBeneficiary(orderId, itemToDelete.beneficiaryId);
            message.success("刪除成功");
            if (onRefresh) {
              onRefresh();
              return;
            }
          } catch (e) {
            console.error(e);
            message.error("刪除失敗");
            return;
          }
        }

        const newData = [...data];
        newData.splice(index, 1);
        onChange(newData);
      },
    });
  };

  const handleSave = async (val: BeneficiaryVO) => {
    console.log("val", val);
    if (orderId) {
      try {
        await updateBeneficiary(orderId, { ...val, orderId });
        message.success("儲存成功");
        if (onRefresh) {
          onRefresh();
          setView("list");
          return;
        }
      } catch (e) {
        console.error(e);
        message.error("儲存失敗");
        return;
      }
    }

    const newData = [...data];
    if (editingIndex !== null) {
      newData[editingIndex] = val;
    } else {
      newData.push(val);
    }
    onChange(newData);
    setView("list");
  };

  useImperativeHandle(ref, () => ({
    validate: async () => {
      if (view === "edit" && editFormRef.current) {
        const values = await editFormRef.current.validate();
        await handleSave(values);
        return values;
      }
      return Promise.resolve();
    },
  }));

  // Styles to match CustomerSelectModal
  const cardStyle: React.CSSProperties = {
    background: "#fff",
    border: "1px solid #e8e8e8",
    borderRadius: 12,
    padding: "16px 24px",
    marginBottom: 16,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    transition: "all 0.3s",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.02)",
    cursor: "pointer",
  };

  const nameStyle: React.CSSProperties = {
    fontSize: 16,
    fontWeight: 600,
    color: "#333",
    marginRight: 12,
  };

  const tagStyle: React.CSSProperties = {
    backgroundColor: "#fff7e6",
    color: "#fa8c16",
    border: "1px solid #ffd591",
    fontSize: 12,
    padding: "1px 8px",
    borderRadius: 4,
    lineHeight: 1.5,
  };

  const detailStyle: React.CSSProperties = {
    fontSize: 13,
    color: "#999",
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    marginTop: 8,
  };

  return (
    <div className="space-y-6">
      {view === "list" ? (
        <>
          {/* Grid Layout for Beneficiary Cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: "20px",
              marginBottom: "24px",
            }}
            className="beneficiary-grid"
          >
            {data.map((item, index) => {
              const relationshipText = getDictLabel(COMMON_DICTS.relationship, item.relationship) || "-";
              // 受益人为本人时不隐藏身份证号码
              const isSelf = item.relationship === "SELF";
              const maskIdNumber = (idNum: string | undefined) => {
                if (!idNum) return "-";
                // 如果是本人，直接返回完整号码
                if (isSelf) return idNum;
                return idNum;
              };

              return (
                <div
                  key={index}
                  style={{
                    background: "#fff",
                    border: "1px solid #E2E8F0",
                    borderRadius: "10px",
                    padding: "24px",
                    boxShadow: "0px 1px 11px 0px rgba(226, 232, 240, 0.6)",
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                  }}
                >
                  {/* Header: Name and Relationship Badge */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: "20px",
                          fontWeight: "600",
                          color: "#141923",
                          lineHeight: "27px",
                        }}
                      >
                        {item.basic?.chineseSurname}
                        {item.basic?.chineseGivenName}
                      </div>
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: "bold",
                          color: "#64748B",
                          lineHeight: "19px",
                          textTransform: "uppercase",
                        }}
                      >
                        {item.basic?.englishSurname} {item.basic?.englishGivenName}
                      </div>
                    </div>
                    <div
                      style={{
                        background: "var(--theme-tertiary)",
                        color: "var(--theme-primary)",
                        fontSize: "14px",
                        fontWeight: 600,
                        lineHeight: "20px",
                        padding: "6px 12px",
                        borderRadius: "6px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      與受保人關係：{relationshipText}
                    </div>
                  </div>
                  <div
                    style={{
                      width: "100%",
                      height: "1px",
                      background: "#F1F5F9",
                    }}
                  ></div>
                  {/* Details: Key-Value Rows */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "14px",
                          color: "#64748B",
                          fontWeight: "500",
                          lineHeight: "17px",
                          width: "120px",
                        }}
                      >
                        受益比例：
                      </span>
                      <span
                        style={{
                          fontSize: "14px",
                          color: "#334255",
                          fontWeight: "500",
                          flex: 1,
                          width: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {/* @ts-expect-error: sharePercentage optional */}
                        {item.percentage ?? 0}%
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "14px",
                          color: "#64748B",
                          fontWeight: "500",
                          lineHeight: "17px",
                          width: "120px",
                        }}
                      >
                        {getMetadataLabel("ID_TYPE", item.basic?.idType, COMMON_DICTS.idType) || "證件類型"}：
                      </span>
                      <span
                        style={{
                          fontSize: "14px",
                          color: "#334255",
                          fontWeight: "500",
                          flex: 1,
                          width: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {maskIdNumber(item.basic?.idNumber)}
                      </span>
                    </div>
                  </div>

                  {/* Buttons */}
                  <div style={{ display: "flex", gap: "12px", marginTop: "4px" }}>
                    {!readOnly ? (
                      <>
                        <CustomButton
                          height={36}
                          bgColor="#fff"
                          color="#666"
                          style={{
                            border: "1px solid #E2E8F0",
                            borderRadius: 6,
                            flex: 1,
                          }}
                          onClick={() => handleDelete(index)}
                        >
                          刪除
                        </CustomButton>
                        <CustomButton
                          height={36}
                          bgColor="var(--theme-primary)"
                          color="#fff"
                          style={{
                            borderRadius: 6,
                            flex: 1,
                          }}
                          onClick={() => handleEdit(index)}
                        >
                          修改
                        </CustomButton>
                      </>
                    ) : (
                      <CustomButton
                        height={36}
                        bgColor="#fff"
                        color="var(--theme-primary)"
                        style={{
                          border: "1px solid var(--theme-primary)",
                          borderRadius: 6,
                        }}
                        onClick={() => handleEdit(index)}
                      >
                        查看
                      </CustomButton>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {data.length === 0 && (
            <div
              style={{
                textAlign: "center",
                padding: "60px 0",
                color: "#999",
                fontSize: 16,
                background: "#fafafa",
                borderRadius: 12,
                border: "1px dashed #e8e8e8",
                marginBottom: "24px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "16px",
              }}
            >
              <div>預設為法定受益人，若需新增個人受益人，請點擊下方按鈕新增</div>
              {!readOnly && (
                <CustomButton height={40} icon={<PlusOutlined />} bgColor="var(--theme-primary)" color="#fff" style={{ borderRadius: 6 }} onClick={handleAdd}>
                  新增受益人
                </CustomButton>
              )}
            </div>
          )}
          {/* Add Beneficiary Button */}
          {!readOnly && data.length > 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "18px",
              }}
            >
              <CustomButton height={40} icon={<PlusOutlined />} bgColor="var(--theme-primary)" color="#fff" style={{ borderRadius: 6 }} onClick={handleAdd}>
                新增受益人
              </CustomButton>
            </div>
          )}
        </>
      ) : (
        <BeneficiaryEditForm
          ref={editFormRef}
          initialData={editingData}
          onSave={handleSave}
          onCancel={() => setView("list")}
          isEdit={editingIndex !== null}
          index={editingIndex !== null ? editingIndex : undefined}
          existingBeneficiaries={existingBeneficiaries}
          readOnly={readOnly}
          policyholder={policyholder}
        />
      )}

      <Modal title="選擇現有受益人" open={selectModalVisible} onCancel={() => setSelectModalVisible(false)} footer={null} width={600}>
        <div style={{ maxHeight: 400, overflowY: "auto", padding: 4 }}>
          {existingBeneficiaries.map((b) => (
            <div
              key={b.beneficiaryId}
              onClick={() => handleSelectExisting(b)}
              style={cardStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--theme-primary)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.08)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e8e8e8";
                e.currentTarget.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.02)";
              }}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 8,
                  }}
                >
                  <span style={nameStyle}>
                    {b.basic?.chineseSurname}
                    {b.basic?.chineseGivenName} ({b.basic?.englishSurname} {b.basic?.englishGivenName})
                  </span>
                  <span style={tagStyle}>{b.basic?.gender === Gender.Male ? "男" : "女"}</span>
                </div>
                <div style={detailStyle}>
                  <span style={{ marginRight: 16, position: "relative" }}>
                    {b.basic?.idNumber}
                    <span
                      style={{
                        position: "absolute",
                        right: -9,
                        top: 0,
                        color: "#e8e8e8",
                      }}
                    >
                      |
                    </span>
                  </span>
                  <span style={{ marginLeft: 8 }}>{b.relationship}</span>
                </div>
              </div>
              <CustomButton height={32} bgColor="var(--theme-primary)" color="#fff" onClick={() => handleSelectExisting(b)}>
                選擇
              </CustomButton>
            </div>
          ))}
          {existingBeneficiaries.length === 0 && <div className="text-center py-8 text-gray-400">暫無可選的現有受益人</div>}
        </div>
      </Modal>
    </div>
  );
});
